import { Eye } from 'lucide-react'
import { FormProvider } from 'react-hook-form'

import { useStockTable } from '@/hooks/features/stock'

import { Button } from '@/components/ui/button'
import { NoData } from '@/components/ui/data-table'
import { Label } from '@/components/ui/label'
import { Pagination } from '@/components/ui/pagination-controls'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { TransactionDetailDialog } from './TransactionDetailDialog'
import { formatDate, getStatusBadge, getTypeBadge } from './utils'

interface Props {
  onOpenPurchaseDialog?: () => void
}

export const StockHistoryTable = ({ onOpenPurchaseDialog }: Props = {}) => {
  const {
    loading,
    data,
    pagination,
    formMethods,
    setPage,
    setPageSize,
    detailDialogOpen,
    setDetailDialogOpen,
    detailData,
    detailLoading,
    handleViewDetail,
    formValues,
  } = useStockTable()

  const { filters } = formValues
  const { setValue } = formMethods

  const setFilter = (key: string, value: any) => {
    setValue(`filters.${key}` as any, value === 'all' ? null : value)
    setValue('pagination.currentPage', 1)
  }

  const openPurchase = onOpenPurchaseDialog ?? (() => {})

  return (
    <FormProvider {...formMethods}>
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="whitespace-nowrap text-sm text-muted-foreground">Type</Label>
              <Select value={filters.type ?? 'all'} onValueChange={(val) => setFilter('type', val)}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="purchase">Purchase</SelectItem>
                  <SelectItem value="adjustment">Adjustment</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label className="whitespace-nowrap text-sm text-muted-foreground">Status</Label>
              <Select
                value={filters.status ?? 'all'}
                onValueChange={(val) => setFilter('status', val)}
              >
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Pagination.PageSizeDropdown
              pageSize={pagination.pageSize}
              onPageSizeChange={setPageSize}
            />
          </div>
        </div>

        <div className="border-t border-border-subtle">
          {!loading && data.length === 0 ? (
            <NoData
              title="No transactions found"
              btnText="New purchase"
              openCreateDialog={openPurchase}
            />
          ) : (
            <>
              <div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading
                      ? Array.from({ length: pagination.pageSize }).map((_, i) => (
                          <TableRow key={i}>
                            {Array.from({ length: 5 }).map((_, j) => (
                              <TableCell key={j}>
                                <Skeleton className="h-4 w-3/4" />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      : data.map((tx) => (
                          <TableRow key={tx._id}>
                            <TableCell>{getTypeBadge(tx.type)}</TableCell>
                            <TableCell>{getStatusBadge(tx.status)}</TableCell>
                            <TableCell className="max-w-60 truncate text-muted-foreground">
                              {tx.description || '—'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap tabular-nums text-muted-foreground">
                              {formatDate(tx.created_at)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="View transaction"
                                onClick={() => handleViewDetail(tx)}
                              >
                                <Eye />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              </div>

              <div className="px-3 pb-3">
                <Pagination.Buttons
                  currentPage={pagination.currentPage}
                  totalItems={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  onPageChange={setPage}
                />
              </div>
            </>
          )}
        </div>

        <TransactionDetailDialog
          open={detailDialogOpen}
          onOpenChange={setDetailDialogOpen}
          data={detailData}
          loading={detailLoading}
        />
      </div>
    </FormProvider>
  )
}
